/**
 * SignUpToInteract — the CTA dialog shown to anonymous visitors when they
 * try to interact (vote, save, react, reply, comment, follow, …) on a
 * guest-viewable page.
 *
 * The dialog never claims that any paid feature is "free"; it just invites
 * the visitor to sign in or create an account. The wrapped action is never
 * executed for a guest, so no API request is fired.
 *
 * RTL: logical inline classes only; the LogIn icon is mirrored with
 * `rtl:scale-x-[-1]`. No row-reversal RTL utility per `.claude/rules/rtl-support-arabic.md`.
 */
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { LogIn, UserPlus } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { Button } from "@shared/ui/button";
import { buildLoginUrlPreservingReturn } from "./returnUrl";
import type { RequireAuthIntent } from "./types";

export interface SignUpToInteractProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  intent: RequireAuthIntent;
  /**
   * Return URL injected into the login/register link's `next` query param.
   * Defaults to the current page so the user lands back where they were.
   */
  returnTo?: string;
}

type IntentCopy = {
  titleKey: string;
  titleFallback: string;
  bodyKey: string;
  bodyFallback: string;
};

const INTENT_COPY: Record<RequireAuthIntent, IntentCopy> = {
  vote: {
    titleKey: "common:authGate.vote.title",
    titleFallback: "Sign in to vote",
    bodyKey: "common:authGate.vote.body",
    bodyFallback:
      "Create a free account to upvote helpful answers in the community.",
  },
  save: {
    titleKey: "common:authGate.save.title",
    titleFallback: "Sign in to save",
    bodyKey: "common:authGate.save.body",
    bodyFallback:
      "Create a free account to bookmark this for later.",
  },
  react: {
    titleKey: "common:authGate.react.title",
    titleFallback: "Sign in to react",
    bodyKey: "common:authGate.react.body",
    bodyFallback:
      "Create a free account to react to posts in the community.",
  },
  reply: {
    titleKey: "common:authGate.reply.title",
    titleFallback: "Sign in to join the conversation",
    bodyKey: "common:authGate.reply.body",
    bodyFallback:
      "Create a free account to reply and discuss with the community.",
  },
  comment: {
    titleKey: "common:authGate.comment.title",
    titleFallback: "Sign in to comment",
    bodyKey: "common:authGate.comment.body",
    bodyFallback:
      "Create a free account to leave a comment.",
  },
  report: {
    titleKey: "common:authGate.report.title",
    titleFallback: "Sign in to report",
    bodyKey: "common:authGate.report.body",
    bodyFallback:
      "Sign in so moderators can follow up with you about this report.",
  },
  share: {
    titleKey: "common:authGate.share.title",
    titleFallback: "Sign in to share",
    bodyKey: "common:authGate.share.body",
    bodyFallback: "Sign in to share this with your network.",
  },
  follow: {
    titleKey: "common:authGate.follow.title",
    titleFallback: "Sign in to follow",
    bodyKey: "common:authGate.follow.body",
    bodyFallback:
      "Create a free account to follow people and topics you care about.",
  },
  create: {
    titleKey: "common:authGate.create.title",
    titleFallback: "Sign in to post",
    bodyKey: "common:authGate.create.body",
    bodyFallback:
      "Create a free account to start a thread, write a recipe, or post a story.",
  },
  contact: {
    titleKey: "common:authGate.contact.title",
    titleFallback: "Sign in to contact a mentor",
    bodyKey: "common:authGate.contact.body",
    bodyFallback:
      "Sign in so mentors can reach you securely through your account.",
  },
  generic: {
    titleKey: "common:authGate.generic.title",
    titleFallback: "Sign in to continue",
    bodyKey: "common:authGate.generic.body",
    bodyFallback:
      "Create a free account to interact. Reading stays free.",
  },
};

export function SignUpToInteract({
  open,
  onOpenChange,
  intent,
  returnTo,
}: SignUpToInteractProps) {
  const { t } = useTranslation(["common", "auth"]);

  const copy = INTENT_COPY[intent] ?? INTENT_COPY.generic;

  const { loginHref, registerHref } = useMemo(
    () => ({
      loginHref: buildLoginUrlPreservingReturn(returnTo, "login"),
      registerHref: buildLoginUrlPreservingReturn(returnTo, "register"),
    }),
    [returnTo],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="signup-to-interact"
        aria-describedby="signup-to-interact-body"
        closeLabel={t("common:close", "Close")}
      >
        <DialogHeader>
          <DialogTitle data-testid="signup-to-interact-title">
            {t(copy.titleKey, copy.titleFallback)}
          </DialogTitle>
          <DialogDescription id="signup-to-interact-body">
            {t(copy.bodyKey, copy.bodyFallback)}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button
            type="button"
            variant="outline"
            asChild
            data-testid="signup-to-interact-login"
          >
            <a href={loginHref}>
              <LogIn
                className="me-2 h-4 w-4 rtl:scale-x-[-1]"
                aria-hidden="true"
              />
              {t("auth:login.signInButton", "Sign In")}
            </a>
          </Button>
          <Button
            type="button"
            asChild
            data-testid="signup-to-interact-register"
          >
            <a href={registerHref}>
              <UserPlus className="me-2 h-4 w-4" aria-hidden="true" />
              {t("common:authGate.cta.createAccount", "Create free account")}
            </a>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default SignUpToInteract;
